import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import TableContainer from '@mui/material/TableContainer'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeeLabelMap } from '../../hooks/useFeeLabelMap'
import {
  Discount,
  Fee,
  SendTuitionInvoiceRequestBody,
  TuitionInvoice,
  TuitionPayment,
} from '../../swagger'
import getLocaleCurrencyForAmount from '../../utils/getLocaleCurrencyForAmount'
import ActionButtons from '../Buttons/ActionButtons'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import TableHeaders from '../Interfaces/TableHeaders'
import ConfirmationModal from '../Modals/ConfirmationModal'
import LedgerTable, { LedgerRow } from '../Table/LedgerTable'
import CardFormHeader from './CardFormHeader'
import { styled } from '@mui/system'
import { LoadingContext } from '../Context/LoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import useLoadingContext from '../../hooks/useLoadingContext'
import { dateToDashString, IS_UTC } from '../../utils/dateUtility'
import CCLogo from '../../Images/Branding/cc-combination-blue.png'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import IconTextButton, {
  IconTextButtonVariant,
} from '../Buttons/IconTextButton'
import PrintIcon from '@mui/icons-material/Print'
import InvoiceSignaturePreview from '../Account/invoiceProfile/InvoiceSignaturePreview'
import {
  convertTwoDecimalStringToBig,
  getBigZero,
} from '../../utils/bigJSHelper'
import Big from 'big.js'

interface StyledCardProps {
  hasOneEnrollment?: boolean //Adjust the zoom level dynamically based on the content and ensure that it fits
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'hasOneEnrollment', // Prevent hasOneEnrollment from being passed to the DOM
})<StyledCardProps>(({ theme, hasOneEnrollment }) => ({
  padding: theme.spacing(3, 4, 4),
  maxWidth: 1200,
  color: theme.palette.primary.main,

  '@media print': {
    height: 'auto',
    width: '100%',
    zoom: hasOneEnrollment ? 0.85 : 1.0, // Adjust to fit content

    '& .qr-img-container': {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: 'none',
      margin: '0',
      padding: '0',
    },

    '& .qr-img-container .image-preview-container, & .qr-img-container .image-preview':
      {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: 'none',
      },

    '& .qr-img-container img': {
      pageBreakBefore: 'avoid',
      pageBreakAfter: 'avoid',
      pageBreakInside: 'avoid',
      breakBefore: 'avoid',
      breakAfter: 'avoid',
      breakInside: 'avoid',
      height: '30%',
      width: '30%',
      border: 'none',
    },
  },
}))

const StyledImg = styled(Box)(({}) => ({
  '@media print': {
    maxWidth: '120px',
  },
}))

export function sumAmounts(values: Fee[]): Big
export function sumAmounts(values: Discount[]): Big
export function sumAmounts(values: TuitionPayment[]): Big
export function sumAmounts(values: (Fee | Discount | TuitionPayment)[]): Big {
  return values.reduce(
    (acc, curr) => acc.plus(curr.amount.amount),
    getBigZero()
  )
}

const MAX_CHAR_LIMIT_FOR_COMMENT = 255

export type DiscountActionType = 'add' | 'edit' | 'remove'

export enum InvoiceVariant {
  /** An editable invoice preview to send. */
  New,
  /** A non-editable invoice that has already been sent. */
  Sent,
}

export interface InvoiceCardProps {
  showSendInvoiceButton: boolean
  isSubLicensedCommunity: boolean
  tuitionInvoice: TuitionInvoice
  onCloseInvoice: () => void
  sendInvoice: (
    body: SendTuitionInvoiceRequestBody,
    callback?: () => void
  ) => void
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  showSendInvoiceButton,
  isSubLicensedCommunity,
  tuitionInvoice,
  onCloseInvoice,
  sendInvoice: sendInvoiceForParent,
}) => {
  const isDesktop = useShowOnDesktop()
  const theme = useTheme()
  const { t } = useTranslation()
  const feeLabelMap = useFeeLabelMap(t)

  const { InvoiceCard } = useLoadingIds()
  const loadingId = InvoiceCard.sendTuitionInvoice
  const { addLoadingIds } = React.useContext(LoadingContext)

  const [
    isSendInvoiceConfirmationModalOpen,
    setIsSendInvoiceConfirmationModalOpen,
  ] = useState(false)

  const [disableConfirmationSubmit, setDisableConfirmationSubmit] =
    useState(false)

  const [comment, setComment] = useState(tuitionInvoice.comment ?? '')

  const currencyCode = tuitionInvoice.currencyCode

  const handleSendInvoiceConfirmation = async (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()

    setDisableConfirmationSubmit(true)

    addLoadingIds([loadingId])
  }

  const handleConfirmationModalClose = () => {
    setDisableConfirmationSubmit(false)
    setIsSendInvoiceConfirmationModalOpen(false)
  }

  const sendInvoice = async () => {
    // Prepare request body
    const body: SendTuitionInvoiceRequestBody = {
      comment: !comment ? undefined : comment,
      invoiceKey: tuitionInvoice.invoiceKey as number,
      // Discounts are now ignored on the backend in favor of entering discounts on the billing tables. There is no need to send an array of discounts.
    }

    sendInvoiceForParent(body, () => {
      handleConfirmationModalClose()
    })
  }

  useLoadingContext({
    asyncFunction: sendInvoice,
    loadingId: loadingId,
  })

  const commonLeftTableHeader: TableHeaders = {
    label: '',
    align: 'left',
    cssProps: {
      color: theme.palette.primary.main,
      paddingLeft: 0,
      borderLeft: '1px solid #fff',
      borderRight: '1px solid #fff',
    },
  }

  const commonInvoiceTableHeader: TableHeaders = {
    ...commonLeftTableHeader,
    align: 'center',
    cssProps: {
      ...commonLeftTableHeader.cssProps,
      padding: theme.spacing(1),
      backgroundColor: theme.palette.hover.main,
    },
  }

  const commonCellCssProps = {
    padding: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderLeft: '1px solid #fff',
    borderRight: '1px solid #fff',
    width: '20%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  const paymentReceived = t('Invoice.PaymentReceived', 'Payment Received')

  const printInvoice = () => {
    window.print()
  }

  const PageButtons = showSendInvoiceButton ? (
    <React.Fragment>
      <TextButton
        id="cancel"
        variant={TextButtonVariant.Cancel}
        onClick={onCloseInvoice}
      />
      <ContainedButton
        id="sendInvoice"
        variant={ContainedButtonVariant.SendInvoice}
        onClick={() => setIsSendInvoiceConfirmationModalOpen(true)}
      />
    </React.Fragment>
  ) : (
    <>
      <IconTextButton
        id="printInvoice"
        variant={IconTextButtonVariant.PrintInvoice}
        startIcon={<PrintIcon />}
        onClick={printInvoice}
      />
      <ContainedButton
        id="closeInvoice"
        variant={ContainedButtonVariant.Close}
        onClick={onCloseInvoice}
      />
    </>
  )

  const confirmationTitle = `${t(
    'Invoice.SendConfirmationModal.Header',
    'Clicking "Send Invoice" will email a copy of the invoice to'
  )} ${tuitionInvoice.parentEmail}`

  const DialogContent = isSubLicensedCommunity &&
    tuitionInvoice &&
    !tuitionInvoice.hasAssignedTutor && (
      <Typography variant="body1" align="center">
        {t(
          'Invoice.SendConfirmationModal.Body',
          '*** Warning: A tutor has not been assigned! Assign a tutor before sending the invoice. If you proceed without assigning a tutor, manual invoice changes will be needed after a tutor is assigned.'
        )}
      </Typography>
    )

  const DialogActions = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.SendInvoice}
      secondaryButtonLabel={TextButtonVariant.Cancel}
      secondaryClick={() => setIsSendInvoiceConfirmationModalOpen(false)}
      disablePrimaryButton={disableConfirmationSubmit}
    />
  )

  const confirmationModal = (
    <ConfirmationModal
      isOpen={isSendInvoiceConfirmationModalOpen}
      dialogTitle={confirmationTitle}
      dialogContent={DialogContent}
      dialogActions={DialogActions}
      handleFormSubmit={handleSendInvoiceConfirmation}
    />
  )

  // if useDefault signature is undefined use default, using nullish coalescing to avoid js coercion
  const showDefaultSignature =
    tuitionInvoice.invoiceSignature?.useDefaultSignature ?? true

  const {
    city = '',
    state = '',
    zip = '',
  } = tuitionInvoice.parentAddress ? tuitionInvoice.parentAddress : {}

  const addressSecondLine = `${city} ${state} ${zip}`

  const { requesterAddress } = tuitionInvoice.enrollments[0]
  const { invoiceSignature } = tuitionInvoice

  const { requesterPhoneNumber, requesterEmail } = tuitionInvoice.enrollments[0]

  const invoiceSignaturePhone = showDefaultSignature
    ? requesterPhoneNumber
    : invoiceSignature?.phoneNumber

  const invoiceSignatureEmail = showDefaultSignature
    ? requesterEmail
    : invoiceSignature?.email

  const noRefundableMessage = t(
    'Invoice.NoRefundableMessage',
    'All tuition and fees are non-refundable. The application fee and membership fee may be transferable to a new community according to relocation guidelines. Membership Fees should be paid directly to Classical Conversations, Inc. through CC Connected prior to attending community.'
  )

  const thanksMessage = showDefaultSignature
    ? t('Invoice.ThanksMessage', 'Thank You!')
    : invoiceSignature?.closingMessage

  const streetAddress1 = showDefaultSignature
    ? tuitionInvoice.enrollments[0].requesterAddress?.streetAddress1
    : invoiceSignature?.address?.streetAddress1

  const InvoiceHeader = (
    <Box role="heading" aria-label="invoiceHeader">
      <Grid container justifyContent="space-between">
        <Grid item xs={4} sm={6}>
          <StyledImg>
            <img
              style={{
                height: 'auto',
                width: '100%',
                maxWidth: '150px',
              }}
              src={CCLogo}
              alt={t('Invoice.Logo', 'Classical Conversations Logo')}
            />
          </StyledImg>
        </Grid>
        <Grid
          item
          xs={8}
          sm={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="subtitle1"
            align="right"
            sx={{
              fontSize: isDesktop ? '2.5em' : '1.5em',
              color: theme.palette.grey[300],
              fontStyle: 'italic',
              fontWeight: 'bolder',
              lineHeight: '0.8em',
            }}
          >
            {`${t('Invoice.Title', 'Statement')}`}
          </Typography>
          <Typography variant="subtitle1">
            {`${t('Invoice.Date', 'Invoice Created')}:`}
            <Box
              component={'span'}
              sx={{
                paddingLeft: theme.spacing(1),
                fontWeight: 'bolder',
              }}
            >
              {tuitionInvoice.invoiceDate &&
                dateToDashString(tuitionInvoice.invoiceDate, IS_UTC)}
            </Box>
          </Typography>
        </Grid>
      </Grid>

      <Typography
        variant="h5"
        sx={{
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(2),
        }}
      >
        {t('Invoice.CommunityName', '{{communityName}} Community', {
          communityName: tuitionInvoice.enrollments[0].communityName,
        })}
      </Typography>

      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bolder',
              lineHeight: '1.3em',
              mr: theme.spacing(1),
            }}
          >
            {`${t('Invoice.BillTo', 'Bill To')}: `}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              lineHeight: '1.3em',
            }}
          >
            {t('Invoice.ParentName', '{{parentName}}', {
              parentName: tuitionInvoice.parentName || '',
            })}
          </Typography>
        </Box>
        <Typography variant="subtitle1" sx={{ lineHeight: '1.3em' }}>
          {tuitionInvoice.parentAddress?.streetAddress1}
        </Typography>
        <Typography variant="subtitle1" sx={{ lineHeight: '1.3em' }}>
          {addressSecondLine}
        </Typography>
        <Typography variant="subtitle1" mt={2}>
          {tuitionInvoice.parentEmail}
        </Typography>
      </Box>
    </Box>
  )

  return (
    <React.Fragment>
      {confirmationModal}
      <StyledCard hasOneEnrollment={tuitionInvoice.enrollments.length === 1}>
        <CardFormHeader header={<></>} buttons={PageButtons} />

        {InvoiceHeader}

        <Grid container>
          <Grid item xs={12} md={12}>
            {tuitionInvoice.enrollments.map(
              ({
                studentKey,
                programKey,
                studentName,
                programType,
                semesterOneStartDate,
                fees,
                discounts = [],
                payments = [],
                total,
                semesterOneDue,
                semesterTwoDue,
              }) => {
                const isChallenge = programType.includes('Challenge')

                const yearOfSemesterOneStartDate = new Date(
                  semesterOneStartDate
                ).getUTCFullYear()

                const programAndStudentName = t(
                  'Invoice.ProgramAndStudentName',
                  'For {{programType}} - {{yearOfSemesterOneStartDate}}: {{studentName}}',
                  {
                    programType,
                    yearOfSemesterOneStartDate,
                    studentName,
                  }
                )

                const studentFees: LedgerRow[] = fees
                  .filter((fee) =>
                    convertTwoDecimalStringToBig(fee.amount.amount).gt(0)
                  )
                  .map((fee, index) => {
                    const { feeType, amount } = fee
                    const { amount: value, currencyCode } = amount
                    const cashAmount = getLocaleCurrencyForAmount(
                      value,
                      currencyCode
                    )
                    const invoiceDate =
                      index === 0
                        ? tuitionInvoice.invoiceDate
                          ? dateToDashString(tuitionInvoice.invoiceDate, IS_UTC)
                          : ''
                        : ''

                    return {
                      cells: [
                        {
                          content: invoiceDate,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: '',
                          align: 'left',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: feeLabelMap[feeType],
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: cashAmount,
                          align: 'right',
                          color: 'main',
                          cellCssProps: { ...commonCellCssProps },
                        },
                      ],
                    }
                  })

                const rows: LedgerRow[] = [...studentFees]
                const emptyCell = ''

                const discountRows: LedgerRow[] = discounts
                  .filter((discount) =>
                    convertTwoDecimalStringToBig(discount.amount.amount).gt(0)
                  )
                  .map(({ desc, amount: amountObject }) => {
                    const amount = getLocaleCurrencyForAmount(
                      amountObject.amount,
                      amountObject.currencyCode
                    )
                    return {
                      cells: [
                        {
                          content: emptyCell,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: emptyCell,
                          align: 'left',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: desc,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: `- ${amount}`,
                          align: 'right',
                          color: 'main',
                          cellCssProps: { paddingRight: theme.spacing(1) },
                        },
                      ],
                    }
                  })
                rows.push(...discountRows)

                rows.push(
                  {
                    cells: [
                      {
                        colSpan: 4,
                        content: t('Invoice.Subtotal', 'Program Subtotal'),
                        align: 'right',
                        color: 'main',
                        cellCssProps: {
                          ...commonCellCssProps,
                          backgroundColor: theme.palette.hover.main,
                        },
                      },
                    ],
                  },
                  {
                    cells: [
                      {
                        content: emptyCell,
                        align: 'left',
                        colSpan: 3,
                        cellCssProps: {
                          backgroundColor: theme.palette.primary.contrastText,
                          border: '0',
                        },
                      },
                      {
                        content: getLocaleCurrencyForAmount(
                          total.amount,
                          currencyCode
                        ),
                        align: 'right',
                        color: 'main',
                        cellCssProps: {
                          backgroundColor:
                            theme.palette.customBackground.progressBar.bottom,
                          paddingRight: theme.spacing(1),
                        },
                      },
                    ],
                  }
                )

                const paymentRows: LedgerRow[] = payments.map(
                  ({ paymentDate, desc, amount }) => {
                    const money = getLocaleCurrencyForAmount(
                      amount.amount,
                      amount.currencyCode
                    )
                    const date = paymentDate
                      ? dateToDashString(paymentDate, IS_UTC)
                      : ''
                    return {
                      cells: [
                        {
                          content: date,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: desc,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: paymentReceived,
                          align: 'left',
                          color: 'medium',
                          cellCssProps: { ...commonCellCssProps },
                        },
                        {
                          content: `- ${money}`,
                          align: 'right',
                          color: 'main',
                          cellCssProps: { ...commonCellCssProps },
                        },
                      ],
                    }
                  }
                )
                rows.push(...paymentRows)

                const totalPayments = payments.reduce(
                  (sum, payment) => sum.plus(payment.amount.amount),
                  getBigZero()
                )
                if (totalPayments.gt(0)) {
                  rows.push(
                    {
                      cells: [
                        {
                          colSpan: 4,
                          content: t(
                            'Invoice.PaymentSubtotal',
                            'Payment Subtotal'
                          ),
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            ...commonCellCssProps,
                            backgroundColor: theme.palette.hover.main,
                          },
                        },
                      ],
                    },
                    {
                      cells: [
                        {
                          content: emptyCell,
                          align: 'left',
                          colSpan: 3,
                          cellCssProps: {
                            backgroundColor: theme.palette.primary.contrastText,
                            border: '0',
                          },
                        },
                        {
                          content: getLocaleCurrencyForAmount(
                            `-${totalPayments}`,
                            currencyCode
                          ),
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            backgroundColor:
                              theme.palette.customBackground.progressBar.bottom,
                            paddingRight: theme.spacing(1),
                          },
                        },
                      ],
                    }
                  )
                }

                if (semesterOneDue) {
                  const s1Label = t(
                    'Invoice.FirstSemesterTotalDue',
                    'First Semester Total Due'
                  )

                  rows.push(
                    {
                      cells: [
                        {
                          content: '',
                          align: 'left',
                          colSpan: 3,
                          cellCssProps: {
                            backgroundColor: theme.palette.primary.contrastText,
                            border: 0,
                          },
                        },
                        {
                          content: s1Label,
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            ...commonCellCssProps,
                            backgroundColor: theme.palette.hover.main,
                            paddingRight: theme.spacing(1),
                          },
                        },
                      ],
                    },
                    {
                      cells: [
                        {
                          content: '',
                          align: 'left',
                          colSpan: 3,
                          cellCssProps: {
                            backgroundColor: theme.palette.primary.contrastText,
                            border: '0',
                          },
                        },
                        {
                          content: getLocaleCurrencyForAmount(
                            semesterOneDue.amount.amount,
                            semesterOneDue.amount.currencyCode
                          ),
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            backgroundColor:
                              theme.palette.customBackground.progressBar.bottom,
                            paddingRight: theme.spacing(1),
                          },
                        },
                      ],
                    }
                  )
                }

                const showSemesterTwo = !!semesterTwoDue && isChallenge
                if (showSemesterTwo && semesterTwoDue) {
                  const s2Label = t(
                    'Invoice.SecondSemesterTotalDue',
                    'Second Semester Total Due'
                  )

                  rows.push(
                    {
                      cells: [
                        {
                          content: '',
                          align: 'left',
                          colSpan: 3,
                          cellCssProps: {
                            backgroundColor: theme.palette.primary.contrastText,
                            border: 0,
                          },
                        },
                        {
                          content: s2Label,
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            ...commonCellCssProps,
                            backgroundColor: theme.palette.hover.main,
                            paddingRight: theme.spacing(1),
                          },
                        },
                      ],
                    },
                    {
                      cells: [
                        {
                          content: '',
                          align: 'left',
                          colSpan: 3,
                          cellCssProps: {
                            backgroundColor: theme.palette.primary.contrastText,
                            border: '0',
                          },
                        },
                        {
                          content: getLocaleCurrencyForAmount(
                            semesterTwoDue.amount.amount,
                            semesterTwoDue.amount.currencyCode
                          ),
                          align: 'right',
                          color: 'main',
                          cellCssProps: {
                            backgroundColor:
                              theme.palette.customBackground.progressBar.bottom,
                            paddingRight: theme.spacing(1),
                          },
                        },
                      ],
                    }
                  )
                }

                return (
                  <Box
                    component="section"
                    aria-labelledby={`studentEnrollment${studentKey}`}
                    key={`${programKey}-${studentKey}`}
                  >
                    <Typography
                      id={`studentEnrollment${studentKey}`}
                      variant="subtitle1"
                      sx={{
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                      }}
                    >
                      {programAndStudentName}
                    </Typography>
                    <TableContainer
                      sx={{
                        padding: theme.spacing(0.4),
                        border: `1px solid ${theme.palette.hover.main}`,
                      }}
                    >
                      <LedgerTable
                        cssProps={{ marginBottom: theme.spacing(0) }}
                        striped
                        tableHeaders={[
                          {
                            ...commonInvoiceTableHeader,
                            label: t('Invoice.Header.Date', 'Date'),
                          },
                          {
                            ...commonInvoiceTableHeader,
                            label: t(
                              'Invoice.Header.PaymentNotes',
                              'Payment Notes'
                            ),
                          },
                          {
                            ...commonInvoiceTableHeader,
                            label: t(
                              'Invoice.Header.Description',
                              'Description'
                            ),
                          },
                          {
                            ...commonInvoiceTableHeader,
                            label: t('Invoice.Header.Amount', 'Amount'),
                          },
                        ]}
                        rows={rows}
                      />
                    </TableContainer>
                  </Box>
                )
              }
            )}
            <IndividualLineItem
              bold
              lineItem={t('Invoice.TotalOwed', 'Total Owed')}
              displayAmount={getLocaleCurrencyForAmount(
                tuitionInvoice.total.amount,
                currencyCode
              )}
            />

            {showSendInvoiceButton ? (
              <TextField
                id="invoice-message"
                label={t(
                  'Invoice.MessageArea',
                  'Additional messages, payment instructions, etc.'
                )}
                fullWidth
                multiline
                margin="normal"
                rows={5}
                variant="filled"
                value={comment}
                inputProps={{ maxLength: MAX_CHAR_LIMIT_FOR_COMMENT }}
                helperText={t(
                  'Invoice.InvoiceCard.Comment.HelperText',
                  '255 character max'
                )}
                onChange={(e) => setComment(e.target.value)}
                disabled={!showSendInvoiceButton}
              />
            ) : (
              <>
                <TableContainer
                  sx={{
                    mt: theme.spacing(2),
                    padding: theme.spacing(0.4),
                    border: `1px solid ${theme.palette.hover.main}`,
                  }}
                >
                  <LedgerTable
                    ariaLabel="invoice-comments-table"
                    cssProps={{ marginBottom: theme.spacing(0) }}
                    tableHeaders={[
                      {
                        ...commonInvoiceTableHeader,
                        label: t('Invoice.Comments', 'Comments'),
                      },
                    ]}
                    rows={[
                      {
                        cells: [
                          {
                            content: comment,
                            align: 'left',
                            color: 'medium',
                            colSpan: 2,
                            cellCssProps: {
                              ...commonCellCssProps,
                              border: '0',
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </TableContainer>
              </>
            )}
            <Box role="region" aria-label="invoiceFooter">
              <Typography variant="body1" component={'p'} mt={4} align="center">
                {noRefundableMessage}
              </Typography>

              <InvoiceSignaturePreview
                invoiceSignature={{
                  payableTo: showDefaultSignature
                    ? tuitionInvoice.enrollments[0].requesterName ?? ''
                    : invoiceSignature?.payableTo ?? '',
                  address: {
                    streetAddress1,
                    streetAddress2: showDefaultSignature
                      ? tuitionInvoice.enrollments[0].requesterAddress
                          ?.streetAddress2
                      : invoiceSignature?.address?.streetAddress2,
                    city: showDefaultSignature
                      ? requesterAddress?.city
                      : invoiceSignature?.address?.city,
                    state: showDefaultSignature
                      ? requesterAddress?.state
                      : invoiceSignature?.address?.state,
                    zip: showDefaultSignature
                      ? requesterAddress?.zip
                      : invoiceSignature?.address?.zip,
                  },
                  closingMessage: thanksMessage,

                  phoneNumber: invoiceSignaturePhone,
                  email: invoiceSignatureEmail,
                  isViewingInvoicePreview: false,
                  qrCodeImage: invoiceSignature?.qrCodeImage,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </StyledCard>
    </React.Fragment>
  )
}

const IndividualLineItem: React.FC<{
  bold?: boolean
  lineItem: string
  displayAmount: string
}> = ({ bold = false, lineItem, displayAmount }) => {
  const theme = useTheme()
  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="flex-end"
      mt={2}
      flexDirection={'column'}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: bold ? theme.palette.primary.main : theme.palette.primary.dark,
          ...(bold && {
            fontWeight: 900,
          }),
          width: '25%',
          padding: theme.spacing(1),
          textAlign: 'right',
          backgroundColor: theme.palette.hover.main,
          marginRight: theme.spacing(0.2),
        }}
      >
        {lineItem}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          ...(bold && {
            fontWeight: 900,
          }),
          color: theme.palette.primary.main,
          padding: theme.spacing(1),
          // We set this to a specific value to ensure the individual line items align
          width: '25%',
          textAlign: 'right',
          backgroundColor: theme.palette.customBackground.progressBar.bottom,
          marginTop: theme.spacing(0.5),
          marginRight: theme.spacing(0.2),
        }}
      >
        {displayAmount}
      </Typography>
    </Box>
  )
}

export default InvoiceCard
